import React, { useMemo, useState } from 'react';
// import moment from 'moment';
import { useMediaQuery } from '@mui/material';
import theme from '@src/styles/theme';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import CommonButton from '@src/new-components/Button';
import useCountdownTimer from '@src/hooks/useCountdownTimer';
import { CODES } from '@src/constants/tasks';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import { COLOR } from '@src/styles/color';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { useTranslation } from 'react-i18next';
import Tooltip from '@src/components/Tooltip';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Typography from '@src/new-components/Typography';
import {
  StyledTask,
  StyledTaskContentLeft,
  StyledTaskContentRight,
  StyledTaskTitle,
} from './index.styled';

const ICON_SIZE = { width: '24px', height: '24px' };
const COMPLETED_ICON_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
  boxSizing: 'border-box',
  borderRadius: '50%',
  width: '32px',
  height: '32px',
};
const INCOMPLETE_ICON_STYLE = {
  ...COMPLETED_ICON_STYLE,
  backgroundColor: COLOR.primary,
  cursor: 'pointer',
};

const getDescription = (task, isCompleted) => {
  // When task is completed, show the description as 'Completed'
  if (isCompleted && task?.code !== CODES.DAILY_LOGIN) {
    return { vi: 'Đã hoàn thành', en: 'Completed' };
  }

  switch (task?.code) {
    case CODES.DAILY_LOGIN:
    case CODES.REFEREE_FIRST_PAID_ORDER:
      return task?.description;
    case CODES.REFER_A_FRIEND:
    case CODES.REFER_A_FRIEND_TO_BUY_THEIR_FIRST_PACKAGE:
      return { vi: 'Không giới hạn', en: 'Unlimited' };
    default:
      return null;
  }
};

const getCountdown = (timer) => {
  if (!timer) return { vi: '', en: '' };
  const hours = timer.hours ?? 0;
  const minutes = timer.minutes ?? 0;
  const seconds = timer.seconds ?? 0;
  const days = timer.days ?? 0;

  const pad2 = (n) => (n < 10 ? `0${n}` : String(n));

  if (!days && hours < 24) {
    const timeString = `${pad2(hours)} : ${pad2(minutes)} : ${pad2(seconds)}`;
    const colored = `<span style="color: ${COLOR.textRejected};">${timeString}</span>`;
    return {
      vi: `Kết thúc sau: ${colored}`,
      en: `Remaining time: ${colored}`,
    };
  }

  return {
    vi: `Còn ${days} ngày`,
    en: `${days} days left`,
  };
};

const getCredits = (task, t, language, credits) => {
  const formattedCredits = credits.toLocaleString(language);

  switch (task?.code) {
    case CODES.DAILY_LOGIN:
      return t('bonusCreditByDay', { credits: formattedCredits });
    case CODES.REFER_A_FRIEND:
    case CODES.REFER_A_FRIEND_TO_BUY_THEIR_FIRST_PACKAGE:
      return t('bonusCreditByUser', { credits: formattedCredits });
    default:
      return t('bonusCredit', { credits: formattedCredits });
  }
};

const Task = ({ task, onClose }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const nowCountdownTimer = useCountdownTimer(task.expiresAt);
  const { charsPerPoint } = useSelector((state) => state.referralConfig);

  const [openPopupInfoTooltipMobile, setOpenPopupInfoTooltipMobile] =
    useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const rewardCredits = useMemo(
    () =>
      task?.rewards?.reduce((sum, { details, type }) => {
        if (type === 'CREDIT') {
          return sum + (details?.amount || 0);
        }
        return sum;
      }, 0) || 0,
    [task?.rewards, charsPerPoint],
  );

  const isCompleted = useMemo(
    () =>
      (task?.maxRepeats > 0 && task?.totalCompletedTimes >= task?.maxRepeats) ||
      task?.code === CODES.DAILY_LOGIN,
    [task],
  );
  const displayDescription = useMemo(
    () => getDescription(task, isCompleted),
    [task, isCompleted],
  );

  const displayCountdown = useMemo(
    () => getCountdown(nowCountdownTimer),
    [nowCountdownTimer],
  );
  const displayCredits = useMemo(
    () => getCredits(task, t, language, rewardCredits),
    [task, t, language, rewardCredits],
  );

  const handleStartTask = () => {
    if (task?.code === CODES.REFERRAL_CODE_APPLIED_SUCCESSFULLY) {
      dispatch(actions.user.updateIsOpeningRedeemReferralDialog(true));
    }
    if (task?.redirectUri) {
      if (task?.code === CODES.CREATE_PROJECT) {
        history.push(task?.redirectUri);
      } else {
        history.push(`/studio${task?.redirectUri}`);
      }
    }

    onClose?.();
    dispatch(actions.layout.setShowOnboardingPopup(false));
  };

  return (
    <StyledTask isCompleted={isCompleted}>
      <StyledTaskContentLeft>
        <img src={task?.icon} alt="task-icon" />
        <StyledTaskTitle>
          <Typography
            className="title-task"
            variant="body-16-medium"
            color={COLOR.darkGray}
          >
            {task.title?.[language]}
          </Typography>
          <Typography
            className="description-task"
            variant="body-14-regular"
            color={COLOR.descriptionUpload}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            {displayDescription?.[language] || (
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: displayCountdown?.[language],
                }}
              />
            )}
            {task.note && (
              <Tooltip
                arrow
                title={task.note?.[language]}
                placement="top"
                maxWidth="100%"
                onClick={() => setOpenPopupInfoTooltipMobile(task.note)}
                zIndex={99999}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: COLOR.navyBlue }}
                />
              </Tooltip>
            )}
          </Typography>
          <div
            className="bonus-credits-mobile"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: displayCredits,
            }}
          />
        </StyledTaskTitle>
      </StyledTaskContentLeft>
      <StyledTaskContentRight>
        <div
          className="bonus-credits"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: displayCredits,
          }}
        />
        {isCompleted ? (
          <div style={COMPLETED_ICON_STYLE}>
            <CheckCircleRoundedIcon sx={{ ...ICON_SIZE, color: COLOR.green }} />
          </div>
        ) : (
          <div onClick={handleStartTask} role="button" tabIndex={0}>
            <Tooltip arrow title={t('start')} placement="top">
              <div style={INCOMPLETE_ICON_STYLE}>
                <KeyboardArrowRightRoundedIcon
                  sx={{ ...ICON_SIZE, color: COLOR.darkBlue }}
                />
              </div>
            </Tooltip>
          </div>
        )}
      </StyledTaskContentRight>

      <NotificationDialogV3
        name={t('detailInfo')}
        title={t('detailInfo')}
        description={
          <Typography
            component="p"
            variant="body-14-regular"
            color={COLOR.descriptionUpload}
          >
            {task.note?.[language]}
          </Typography>
        }
        hideDangerouslySetInnerHTML
        open={!!openPopupInfoTooltipMobile && isMobile}
        onClose={() => setOpenPopupInfoTooltipMobile(false)}
        variant="null"
        fitWidth
        actionComponent={
          <CommonButton
            onClick={() => setOpenPopupInfoTooltipMobile(false)}
            variant="contained"
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              padding: '8px 24px',
              width: '146px',
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
          >
            {t('gotIt')}
          </CommonButton>
        }
      />
    </StyledTask>
  );
};

export default Task;
